import { yupResolver } from '@hookform/resolvers/yup';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputAdornment,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import UserContext from '../../../../context/userContext';
import { TenantDeployForm } from '../types';

const TenantSchema = Yup.object().shape({
	tenantName: Yup.string().trim().required('Required'),
	file: Yup.mixed().test({
		test: (value) => {
			if (!value || value.length === 0 || !value[0]?.name) {
				return false;
			} else {
				return true;
			}
		},
		message: 'Required',
	}),
});

interface Props {
	initialValues: TenantDeployForm;
	handleSubmit: SubmitHandler<TenantDeployForm>;
	handleClose: () => void;
	isLoading: boolean;
	uploadProgress?: number;
}

const TenantForm = (props: Props) => {
	const { selectedUserRole: user } = React.useContext(UserContext);
	const {
		isLoading,
		handleSubmit,
		initialValues,
		uploadProgress,
		handleClose,
	} = props;
	const {
		register,
		control,
		handleSubmit: useFormSubmit,
	} = useForm({
		defaultValues: initialValues,
		resolver: yupResolver(TenantSchema),
	});

	return (
		<Box
			sx={{ pt: 1, pl: 1, pr: 1, pb: 2 }}
			component='form'
			onSubmit={useFormSubmit(handleSubmit)}
		>
			<Stack spacing={2}>
				<Controller
					control={control}
					name='tenantName'
					render={({ field, fieldState: { error } }) => (
						<TextField
							{...field}
							label='Tenant Name'
							variant='outlined'
							fullWidth
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										{user.namespace}.
									</InputAdornment>
								),
							}}
							error={!!error}
							helperText={
								!!error
									? error?.message
									: 'Using an existing tenant name will replace the config'
							}
							disabled={isLoading}
							onChange={(event) => {
								const clientId = Yup.string()
									.lowercase()
									.transform((currentValue) =>
										currentValue.split(' ').join('-'),
									)
									.cast(event.target.value);
								field.onChange(clientId);
							}}
						/>
					)}
				/>

				<Controller
					control={control}
					name='file'
					render={({ field, fieldState: { error } }) => (
						<FormControl sx={{ m: 3 }} error={!!error} variant='standard'>
							<Box width='100%'>
								<label htmlFor='config-file-upload'>
									<Button
										component='span'
										disabled={isLoading}
										fullWidth
										variant='outlined'
										color='secondary'
										startIcon={<FileUploadIcon />}
									>
										{'Select Config File'}
									</Button>
								</label>
								<input
									{...register('file')}
									disabled={isLoading}
									type='file'
									accept='application/zip'
									id='config-file-upload'
									style={{ display: 'none' }}
								/>
							</Box>
							{!!error && error.message && (
								<FormHelperText sx={{ ml: '14px', mr: '14px' }}>
									{error.message}
								</FormHelperText>
							)}
							{field.value && field?.value[0]?.name && (
								<Typography
									variant='caption'
									sx={{
										display: 'flex',
										alignItems: 'center',
										mt: 2,
									}}
								>
									<FilePresentIcon />
									<Box
										sx={{
											whiteSpace: 'nowrap',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
										}}
									>
										{field.value[0].name}
									</Box>
								</Typography>
							)}
						</FormControl>
					)}
				/>
				<Controller
					control={control}
					name='useProductVersioning'
					render={({ field: { value, onChange } }) => (
						<FormControlLabel
							control={
								<Checkbox
									sx={{ ml: '-12px' }}
									checked={value}
									onChange={onChange}
								/>
							}
							label='Use Product Versioning'
							disabled={isLoading}
						/>
					)}
				/>
			</Stack>
			<Stack direction='row' justifyContent='flex-end' mt={2} spacing={2}>
				<Button color='inherit' onClick={handleClose} disabled={isLoading}>
					Cancel
				</Button>
				<Button
					variant='contained'
					color='secondary'
					type='submit'
					disabled={isLoading}
					sx={{
						'&:disabled': {
							backgroundColor: 'secondary.main',
							color: 'white',
						},
					}}
				>
					{isLoading
						? uploadProgress === 100
							? 'Deploying...'
							: 'Uploading: ' + uploadProgress + '%'
						: 'Deploy'}
				</Button>
			</Stack>
		</Box>
	);
};

export default TenantForm;
